import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import useApi from '@/hooks/useApi/useApi';
import { queryKeys } from '@/lib/queryKeys';
import { track } from '@/modules/Analytics';
import {
  PlanningCache,
  applyIntervalUpdate,
} from '@/modules/Planning/hooks/usePlanningProjects/planningCacheUtils';
import {
  Interval,
  IntervalCompletionState,
  IntervalCompletionStateStatus,
  Project,
} from '@/types/types';

export const useIntervalCompletionStates = (intervalId: Interval['id']) => {
  const { axiosInstance } = useApi();

  return useQuery({
    queryKey: queryKeys.intervals.completionStates(intervalId).queryKey,
    queryFn: async () => {
      const { data } = await axiosInstance.get<IntervalCompletionState[]>(
        `/api/v1/intervals/${intervalId}/completion_states`
      );
      return data;
    },
    enabled: !!intervalId,
  });
};

export const useCreateIntervalCompletionState = (intervalId: Interval['id']) => {
  const { axiosInstance } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { percentage: number; notes?: string }) => {
      const { data } = await axiosInstance.post<IntervalCompletionState>(
        `/api/v1/intervals/${intervalId}/completion_states`,
        { interval_completion_state: payload }
      );
      return data;
    },
    onMutate: async ({ percentage }) => {
      const queryKey = queryKeys.planningProjects._def;
      const snapshot = queryClient.getQueriesData<PlanningCache>({ queryKey });

      queryClient.setQueriesData<PlanningCache>({ queryKey }, (old) =>
        old
          ? applyIntervalUpdate(old, intervalId, {
              completion_percentage: percentage,
              ...(percentage === 100 ? { completed: true } : {}),
            })
          : old
      );

      return { snapshot };
    },
    onError: (_err, _vars, context) => {
      context?.snapshot?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
    onSuccess: () => {
      track('progress_reported');
      queryClient.invalidateQueries({
        queryKey: queryKeys.intervals.completionStates(intervalId).queryKey,
      });
      // Task detail drawer, task lists (Kanban/tables), and the Gantt chart
      // all show the same derived completion % — taskId isn't known here,
      // so invalidate every cached task rather than threading it through
      // every call site.
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks._def });
    },
  });
};

export const useReviewIntervalCompletionStateForItem = () => {
  const { axiosInstance } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      intervalId,
      stateId,
      status,
    }: {
      intervalId: Interval['id'];
      projectId: Project['id'];
      stateId: IntervalCompletionState['id'];
      status: Extract<IntervalCompletionStateStatus, 'approved' | 'declined'>;
    }) => {
      const { data } = await axiosInstance.patch<IntervalCompletionState>(
        `/api/v1/intervals/${intervalId}/completion_states/${stateId}`,
        { interval_completion_state: { status } }
      );
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.intervals.completionStates(variables.intervalId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.projects.pendingIntervalCompletionStates(variables.projectId).queryKey,
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.planningProjects._def });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks._def });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard._def });
    },
  });
};
